// phaseEvents — singleton bus + promise helper for awaiting backend
// phase completion via the WebSocket stream.
//
// Background: long-running phase routes (Phase 2/3/4/5) now accept
// ``?async=true`` and return 202 immediately. The frontend awaits the
// matching ``phase_completed`` (or ``error``) event on
// ``/ws/projects/{id}/events`` to learn when the work finished. That
// stream is already consumed by ``useProjectEvents``, which forwards
// every message into this bus so the runner mutation in
// ``PipelineRunner`` can subscribe without opening a second WebSocket.
//
// The bus is a module-singleton on purpose: there's only ever one WS
// stream open per page (a single ``useProjectEvents`` mount in
// CommandCenterPage), and the mutation lives next to the stepper —
// both share the same bus instance.
//
// Resilience: the WS stream is best-effort. If the connection drops
// while the backend is mid-phase and reconnects after
// ``phase_completed`` fired, the event is gone (EventHub queues are
// per-subscriber and torn down on disconnect). To stop that scenario
// from leaving the UI spinning forever, ``waitForPhaseCompletion``
// races the WS event against a periodic poll of ``run-state.json`` —
// whichever signal lands first wins. The poll synthesises a
// ``phase_completed``-shaped event from the persisted run-state so
// the caller's ``onSuccess`` handler doesn't need to know which path
// it took.

import { api } from './api'
import type { PhaseSummaryRecord, WSEvent } from '@/types'

type Listener = (event: WSEvent) => void

const listeners = new Set<Listener>()

export const phaseEventBus = {
  emit(event: WSEvent): void {
    listeners.forEach((l) => {
      try {
        l(event)
      } catch {
        // A listener throwing must not affect the others or stop the
        // WS pump. Swallow.
      }
    })
  },
  on(listener: Listener): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
}

export interface WaitForPhaseOptions {
  /** Hard upper-bound on the wait, in milliseconds. Defaults to 30 min
   *  to cover the slowest currently-known phase (Phase 4 + Rewriter
   *  pre-pass on a large SRD). At the deadline the promise rejects with
   *  :class:`PhaseTimeoutError` regardless of which signal source we
   *  were waiting on. */
  timeoutMs?: number
  /** Period between run-state polls, in milliseconds. The poll catches
   *  the case where the WS event fired while the browser was
   *  disconnected (server restart, transient network blip) — in the
   *  happy path the WS event arrives first and the poll never runs to
   *  completion. Defaults to 5 s — small enough that a missed event
   *  costs at most 5 s of extra spinner, large enough that an idle
   *  Phase-4 (10 min) only racks up ~120 polls. */
  pollIntervalMs?: number
}

export class PhaseTimeoutError extends Error {
  constructor(public readonly phase: number, public readonly timeoutMs: number) {
    super(
      `Phase ${phase} did not complete within ${Math.round(timeoutMs / 1000)}s. ` +
        `The server may still be working — refresh to check the artifact index.`,
    )
    this.name = 'PhaseTimeoutError'
  }
}

export class PhaseFailedError extends Error {
  constructor(
    public readonly phase: number,
    public readonly code: string,
    message: string,
    public readonly detail?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'PhaseFailedError'
  }
}

/**
 * Build a synthetic ``phase_completed`` event from a run-state phase
 * record so the caller doesn't have to switch on "did this come from
 * the WS or the poll fallback?". The fields mirror what the backend
 * stamps onto the real ``phase_completed`` payload (see
 * ``_phase_{N}_body`` in ``src/ai_alm/api/routes/phases.py``).
 */
function synthesisePhaseCompletedEvent(
  projectId: string,
  runId: number,
  phase: number,
  summary: PhaseSummaryRecord,
): WSEvent {
  const payload: Record<string, unknown> = {
    artifacts: summary.artifacts ?? {},
  }
  if (summary.rqs_overall != null) payload.rqs_overall = summary.rqs_overall
  if (summary.total_requirements != null)
    payload.total_requirements = summary.total_requirements
  if (summary.rewrite_count != null) payload.rewrite_count = summary.rewrite_count
  if (summary.template_name != null) payload.template_name = summary.template_name
  if (summary.misfit_count != null) payload.misfit_count = summary.misfit_count
  if (summary.total_bytes != null) payload.total_bytes = summary.total_bytes
  if (summary.rendered_diagrams != null)
    payload.rendered_diagrams = summary.rendered_diagrams
  if (summary.constraint_count != null)
    payload.constraint_count = summary.constraint_count
  if (summary.low_confidence_count != null)
    payload.low_confidence_count = summary.low_confidence_count
  if (summary.written_files != null) payload.written_files = summary.written_files
  if (summary.tokens_used) payload.tokens_used = summary.tokens_used
  return {
    event: 'phase_completed',
    project_id: projectId,
    run_id: runId,
    phase,
    payload,
    ts: summary.completed_at ?? new Date().toISOString(),
  }
}

/**
 * Resolve when a ``phase_completed`` arrives for ``(runId, phase)``,
 * either via the WS event bus OR via a periodic poll of
 * ``run-state.json`` (whichever lands first). Reject when an ``error``
 * event arrives, when ``run-state`` shows ``status: "error"``, or when
 * the timeout elapses.
 *
 * Subscribing BEFORE the POST is the caller's responsibility — see
 * ``PipelineRunner``'s mutation wrapper. Otherwise a very fast phase
 * could complete before the listener is wired up.
 */
export function waitForPhaseCompletion(
  projectId: string,
  runId: number,
  phase: number,
  opts: WaitForPhaseOptions = {},
): Promise<WSEvent> {
  const timeoutMs = opts.timeoutMs ?? 30 * 60 * 1000
  const pollIntervalMs = opts.pollIntervalMs ?? 5_000

  return new Promise<WSEvent>((resolve, reject) => {
    let settled = false

    const cleanup = () => {
      clearTimeout(timer)
      clearInterval(pollTimer)
      unsubscribe()
    }

    const settleResolve = (value: WSEvent) => {
      if (settled) return
      settled = true
      cleanup()
      resolve(value)
    }

    const settleReject = (error: Error) => {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }

    const timer = setTimeout(() => {
      settleReject(new PhaseTimeoutError(phase, timeoutMs))
    }, timeoutMs)

    const unsubscribe = phaseEventBus.on((ev) => {
      if (ev.run_id !== runId || ev.phase !== phase) return
      if (ev.event === 'phase_completed') {
        settleResolve(ev)
      } else if (ev.event === 'error') {
        const payload = ev.payload ?? {}
        const code = typeof payload.code === 'string' ? payload.code : `phase${phase}.failed`
        const message =
          typeof payload.message === 'string'
            ? payload.message
            : `Phase ${phase} reported an error.`
        settleReject(new PhaseFailedError(phase, code, message, payload))
      }
    })

    // Run-state poll — the resilience fallback. Reads the ledger the
    // backend writes AFTER it publishes the WS event, so a successful
    // poll can only happen if the WS event was missed (otherwise the
    // listener above already settled the promise).
    const pollOnce = async () => {
      if (settled) return
      try {
        const state = await api.getRunState(projectId, runId)
        if (settled) return
        const phaseState = state.phases?.[String(phase)]
        if (!phaseState) return
        if (phaseState.status === 'done') {
          settleResolve(
            synthesisePhaseCompletedEvent(projectId, runId, phase, phaseState),
          )
        } else if (phaseState.status === 'error') {
          settleReject(
            new PhaseFailedError(
              phase,
              `phase${phase}.failed`,
              phaseState.error_message ?? `Phase ${phase} reported an error.`,
            ),
          )
        }
      } catch {
        // Polling is best-effort — a transient API failure shouldn't
        // kill the wait. Next tick will retry.
      }
    }

    const pollTimer = setInterval(pollOnce, pollIntervalMs)
  })
}
